<script setup lang="ts">
import { computed } from 'vue';
import { Table as ATable } from 'ant-design-vue';
import { array, func, object } from 'vue-types';

import useFileAiReviewsColumns from './Search/useFileAiReviewsColumns';

import type { FileAiReviewFragmentFragment, FileFragmentFragment } from '@/graphql/generated/graphql';
import Table, { props as properties } from '@/ui/Table/Table.vue';
import TableSizeSelect from '@/ui/Table/TableSizeSelect.vue';
import TableColumnSelect from '@/ui/Table/TableColumnSelect.vue';
import useConfig from '@/providers/config/useConfig';
import TableColumn from '@/ui/Table/templates/TableColumn.vue';
import Refetch from '@/ui/Table/templates/Refetch.vue';
import ViewIconButton from '@/ui/ViewIconButton.vue';
import CopyIconButton from '@/ui/CopyIconButton.vue';
import StatusGeneric from '@/ui/StatusGeneric.vue';

const props = defineProps({
  ...properties,
  dataSource: array<FileAiReviewFragmentFragment>().isRequired,
  getLink: func<(data: FileAiReviewFragmentFragment) => string>(),
  onClick: func<(data: FileAiReviewFragmentFragment) => void>(),
  onSelect: func<(data: FileAiReviewFragmentFragment) => void>(),
  refetch: func(),
  file: object<FileFragmentFragment>().isRequired,
});
const {
  columnsSelected,
  columnsFilter,
  setSorter,
  columns,
} = useFileAiReviewsColumns({
  orderBy: props.query.orderBy,
});
const config = useConfig();
const tableDensity = computed(() => config.config.tableDensity);
</script>
<template>
  <Table
    :set-sorter="setSorter"
    placeholder="Search AI review rows"
    top-pag-class="flex flex-wrap items-center justify-between gap-4 mb-4"
    :pagination="pagination"
    :loading="loading"
    :query="query"
    :set-query="setQuery"
    :search="search"
    :set-search="setSearch"
    :hide-search="hideSearch"
    :header-class="headerClass"
  >
    <template #top_pag_left>
      <slot name="top_pag_left" />
    </template>
    <template #top_pag_right>
      <div class="flex space-x-2">
        <TableColumnSelect v-model="columnsSelected" :columns-filter="columnsFilter" />
        <TableSizeSelect />
        <Refetch v-if="refetch" :refetch="refetch" />
      </div>
    </template>
    <template #default="{ handleTableChange }">
      <ATable
        :data-source="dataSource"
        :columns="columns"
        :row-key="(record) => record.id"
        :pagination="false"
        :size="tableDensity"
        @change="handleTableChange"
      >
        <template #bodyCell="{ record, column }">
          <template v-if="column.key === 'onView'">
            <FileAiReviewViewButton
              :file-ai-review="(record as FileAiReviewFragmentFragment)"
              :file="file"
            />
          </template>
          <template v-else-if="column.key === 'error_response'">
            <div v-if="record.error_response" class="flex items-center gap-2">
              <ViewIconButton title="Error response" size="small">
                {{ record.error_response }}
              </ViewIconButton>
              <CopyIconButton :value="record.error_response" size="small" />
            </div>
          </template>
          <template v-else-if="column.key === 'parsed_response'">
            <CopyIconButton v-if="record.parsed_response" :value="record.parsed_response" size="small" />
          </template>
          <template v-else-if="column.key === 'status'">
            <StatusGeneric :status="record.status" />
          </template>
          <TableColumn
            v-else
            :column="(column.key as string)"
            :record="record"
            :get-link="getLink"
            :on-click="onClick"
          />
        </template>
      </ATable>
    </template>
  </Table>
</template>
